// SANDESA storefront track snippet.
//
// A self-contained plain-JS ES module, hosted as a static asset and included in the
// Shopify theme via one `<script type="module">` tag. See
// docs/integrations/storefront-track-snippet.md for the install steps.
//
// Layout: the four functions below are PURE (no browser globals) and are the ones
// unit-tested from tests/track-snippet.test.ts under vitest/node. Everything after the
// "Browser glue" divider touches window/document/localStorage/fetch and only runs in an
// actual browser — the auto-run at the bottom is guarded so importing this module under
// vitest never executes it.

/**
 * Resolve which identity to attach to a tracked event.
 *
 * Precedence: a logged-in customer's email wins over a signed an_k token (from an email
 * link), which wins over a previously-stored email (from a past form submit) — a logged-in
 * customer always wins, so a stale an_k token from a prior click (or a shared device) can
 * never outrank someone who is currently signed in. Empty strings are treated as absent.
 * Returns null when nothing is known — callers must not fire an event for an anonymous
 * visitor.
 *
 * @param {{ customerEmail?: string|null, anK?: string|null, storedEmail?: string|null }} input
 * @returns {{ email?: string, an_k?: string } | null}
 */
export function resolveIdentity({ customerEmail, anK, storedEmail } = {}) {
  if (customerEmail) return { email: customerEmail };
  if (anK) return { an_k: anK };
  if (storedEmail) return { email: storedEmail };
  return null;
}

/**
 * Read the `an_k` value out of a URL query string (e.g. `location.search`).
 *
 * @param {string} search
 * @returns {string | null}
 */
export function parseAnK(search) {
  const params = new URLSearchParams(search || '');
  const value = params.get('an_k');
  return value ? value : null;
}

/**
 * Return `search` with the `an_k` param removed, keeping every other param. Used to scrub
 * the token out of the visible URL (via history.replaceState) once it has been captured,
 * so it never lingers in browser history or a shared link.
 *
 * @param {string} search
 * @returns {string}
 */
export function stripAnK(search) {
  const params = new URLSearchParams(search || '');
  params.delete('an_k');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * Build the `track` request body for a product_viewed event.
 *
 * @param {{ handle: string, title: string, price: string|number|null, url: string }} product
 * @param {{ email?: string, an_k?: string }} identity
 * @returns {Record<string, unknown>}
 */
export function buildProductViewedBody(product, identity) {
  return {
    type: 'event',
    metric: 'product_viewed',
    ...identity,
    properties: {
      handle: product.handle,
      title: product.title,
      price: product.price,
      url: product.url,
    },
  };
}

// ---------------------------------------------------------------------------------------
// Browser glue — everything below reads/writes window, document, localStorage, and fetch.
// None of it is unit-tested directly (no browser globals under vitest/node); it is kept
// deliberately thin and is verified by the operator's live checks per the install doc.
// ---------------------------------------------------------------------------------------

const DEFAULT_FORM_SELECTOR = 'form[data-an-signup], form[action*="/contact"]';

/**
 * Read the snippet's own <script> tag config (its `data-*` attributes) plus the
 * customer-email global the theme's Liquid injects.
 *
 * @returns {{ endpoint: string, siteKey: string, customerEmail: string|null, formSelector: string, product: any }}
 */
export function readConfig() {
  let endpoint = '';
  let siteKey = '';
  let formSelector = DEFAULT_FORM_SELECTOR;
  try {
    const script = document.currentScript
      || document.querySelector('script[data-endpoint][data-site-key]');
    if (script) {
      endpoint = script.dataset.endpoint || '';
      siteKey = script.dataset.siteKey || '';
      formSelector = script.dataset.formSelector || DEFAULT_FORM_SELECTOR;
    }
  } catch {
    // DOM access can fail in unusual embedding contexts; leave defaults.
  }

  let customerEmail = null;
  try {
    customerEmail = window.__AN_CUSTOMER_EMAIL__ || null;
  } catch {
    // ignore
  }

  let product = null;
  try {
    product = window.__AN_PRODUCT__ || null;
  } catch {
    // ignore
  }

  return { endpoint, siteKey, customerEmail, formSelector, product };
}

function readLocalStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage may be unavailable (private browsing, quota, disabled) — never throw.
  }
}

function hookSignupForms(formSelector) {
  try {
    const forms = document.querySelectorAll(formSelector);
    forms.forEach((form) => {
      form.addEventListener('submit', () => {
        try {
          const input = form.querySelector('input[type="email"], input[name*="email" i]');
          const value = input && typeof input.value === 'string' ? input.value.trim() : '';
          if (value) writeLocalStorage('an_email', value);
        } catch {
          // never let a listener throw and break the storefront's own form handling.
        }
      });
    });
  } catch {
    // querySelectorAll can throw on a malformed selector — never break the page for it.
  }
}

/**
 * Wire up the snippet: capture an an_k from the URL, resolve identity, hook signup forms,
 * and (on a product page, with a known identity) POST a product_viewed event.
 *
 * @param {{ endpoint: string, siteKey: string, customerEmail: string|null, formSelector: string, product: any }} cfg
 */
export function start(cfg) {
  try {
    const anKFromUrl = parseAnK(location.search);
    if (anKFromUrl) {
      writeLocalStorage('an_id', anKFromUrl);
      try {
        const cleanSearch = stripAnK(location.search);
        history.replaceState(null, '', location.pathname + cleanSearch + location.hash);
      } catch {
        // history API can be restricted in some embeds — the stored token is enough.
      }
    }
  } catch {
    // location access should never fail, but never let it break the rest of start().
  }

  const identity = resolveIdentity({
    customerEmail: cfg && cfg.customerEmail,
    anK: readLocalStorage('an_id'),
    storedEmail: readLocalStorage('an_email'),
  });

  hookSignupForms((cfg && cfg.formSelector) || DEFAULT_FORM_SELECTOR);

  if (identity && cfg && cfg.product && cfg.endpoint) {
    try {
      fetch(cfg.endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-ant-site-key': cfg.siteKey || '',
        },
        body: JSON.stringify(buildProductViewedBody(cfg.product, identity)),
      }).catch(() => {
        // network failures must never surface to the storefront.
      });
    } catch {
      // fetch itself can throw synchronously in rare environments.
    }
  }
}

// Guarded auto-run: only executes in an actual browser. Under vitest (node, no `window`),
// importing this module is side-effect-free — nothing here runs.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  start(readConfig());
}
